import type { PlausibilityMode } from "@sitescene/types";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";
import { useOrgSelection } from "@/context/use-org-selection";

export interface ShadowTenantSettings {
	alertSeverityThreshold: number;
	defaultFilterMode: string;
	plausibilityMode: PlausibilityMode;
	sensorHealthCheckEnabled: boolean;
}

/** Staff-only tenant settings of the selected organization. */
export const usePlausibilitySettings = () => {
	const { selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? null;
	return useQuery({
		enabled: orgCode !== null,
		queryFn: async () => {
			const response = await apiClient.get<ShadowTenantSettings>(
				ENDPOINTS.shadow.tenantSettings,
			);
			return response.data;
		},
		queryKey: queryKeys.plausibility.settings(orgCode ?? ""),
	});
};

export const useUpdatePlausibilityMode = () => {
	const queryClient = useQueryClient();
	const { selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? "";
	return useMutation({
		mutationFn: async (plausibilityMode: PlausibilityMode) => {
			const response = await apiClient.patch<{
				plausibilityMode: PlausibilityMode;
			}>(ENDPOINTS.shadow.tenantSettings, { plausibilityMode });
			return response.data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: queryKeys.plausibility.settings(orgCode),
			});
		},
	});
};
